import React, { useState, useCallback, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { customSelectStyles_dark, customSelectStyles_light } from '../ui/CustomSelect';

const libraries = ['places'];

const AddressAutocomplete = ({ onSelect, apiKey, placeholder = 'Search address...', styles }) => {
    const { currentTheme } = useSelector((state) => state.theme);
    const [inputValue, setInputValue] = useState('');
    const autocompleteService = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries,
    });

    const loadOptions = useCallback(
        (inputValue, callback) => {
            if (!isLoaded || !autocompleteService.current || inputValue.trim() === '') {
                callback([]);
                return;
            }

            // const request = {
            //     input: inputValue,
            //     componentRestrictions: { country: 'us' }, // Optional: restrict to specific country
            // };
            const request = {
                input: inputValue
            };
            autocompleteService.current.getPlacePredictions(
                request,
                (predictions, status) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                        callback([]);
                        return;
                    }

                    const options = predictions.map((prediction) => ({
                        value: prediction.place_id,
                        label: prediction.description,
                        prediction,
                    }));

                    callback(options);
                }
            );
        },
        [isLoaded]
    );

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        return newValue;
    };

    const handleChange = (selectedOption) => {
        if (!selectedOption) {
            onSelect(null);
            return;
        }

        const placesService = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );

        placesService.getDetails(
            { placeId: selectedOption.value },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    const location = {
                        address: place.formatted_address,
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                        placeId: place.place_id,
                        details: place, // Full place details if needed
                    };
                    onSelect(location);
                }
            }
        );
    };

    // Initialize the autocomplete service when script is loaded
    React.useEffect(() => {
        if (isLoaded) {
            autocompleteService.current =
                new window.google.maps.places.AutocompleteService();
        }
    }, [isLoaded]);

    const defaultStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#94a3b8',
            },
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            marginTop: '4px',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#e2e8f0' : 'white',
            color: state.isSelected ? 'white' : '#1e293b',
            '&:active': {
                backgroundColor: '#2563eb',
                color: 'white',
            },
        }),
    };

    return (
        <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            defaultOptions={false}
            onInputChange={handleInputChange}
            onChange={handleChange}
            inputValue={inputValue}
            placeholder={placeholder}
            isClearable
            noOptionsMessage={() => 'Type to search'}
            loadingMessage={() => 'Loading...'}
            styles={{
                ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                menuPortal: base => ({
                    ...base,
                    zIndex: 9999,
                })
            }}
            width="200px"
        />
    );
};

AddressAutocomplete.propTypes = {
    onSelect: PropTypes.func.isRequired,
    apiKey: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    styles: PropTypes.object,
};

export default AddressAutocomplete;